import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent, useRef } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import Button from '@/components/Button';
import { FormLayout, Input, Label, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { SubtitlesGenerationPrompt } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

const PromptDiv = styled(Input)`
  white-space: pre-line;
  height: auto;
  display: block;
  padding-top: 12px;
  padding-bottom: 12px;
  line-height: 1.4;
  font-weight: 400;

  [contenteditable='true'] {
    padding: 8px;
    outline: 1px dashed ${({ theme }) => theme.colors.gray};
    background-color: ${({ theme }) => theme.colors.grayscaleLight};
  }

  b {
    font-weight: 700;
  }
`;

const StyledSidePanel = styled(SidePanel)`
  width: 800px;
`;

const defaultPrompt = `You are provided with images from a video  taken at {frequency} second intervals, along with their URLs.

  <b>*Task*</b>

  <div contenteditable="true">Your task is to create short and engaging captions for each image to enhance the viewing experience of the video. You are a short reels video narrator providing insights that keep viewers interested.</div>

  <b>*OBJECTIVE*</b>

  <div contenteditable="true">Create concise, engaging captions for each image to appeal to 30-year-olds living in major U.S. coastal cities who enjoy relatable and entertaining content.</div>

  {video_context}

  <b>**Instructions**</b>

- **For Each Caption**:
<div contenteditable="true">- Keep it concise (preferably 10 words or fewer).
- Focus on what's visually interesting in each image, considering the mood and tone conveyed through the visuals.
- While keeping it creative, avoid being overly sensational.
- Avoid using possessive pronouns like "our," "we," or "my," but engaging viewers with "you" or "your" is acceptable.
- Use exclamation points where appropriate to add energy, but avoid overusing them to maintain impact.
- Ensure captions flow cohesively from one to the next:
- If possible, create a cohesive story.
- If not, ensure smooth transitions so they are not standalone sentences.
- Only in the last image URL caption, include a short, intriguing question to engage viewers.</div>
- Use a conversational, {tone} tone.
- **Formatting**:
- Provide a JSON object where each image URL is a key, and its caption is the value.
- Use the following format exactly:

{"script": { "image1_url": "caption1", "image2_url": "caption2", "image3_url": "caption3", ...}}

DO NOT include any explanation, markdown, or extra formatting. Simply return the JSON.

<b>**Validation**</b>

- Double-check the output. Each image URL should have only one caption.
- Ensure strict adherence to these instructions before submitting.

image_url_list = {imagesString}
`;

type FormDataT = {
  title: string;
};

interface Props {
  onClose: () => void;
  promptResource?: SubtitlesGenerationPrompt;
}

const Form: FC<Props> = ({ onClose, promptResource }) => {
  const alerts = useAlerts();
  const queryClient = useQueryClient();
  const divRef = useRef<HTMLDivElement>(null);

  const {
    handleSubmit,
    setError,
    control,
    clearErrors,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      title: promptResource?.title ?? '',
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    handleSubmit(async ({ title }) => {
      const data = {
        title,
        text: divRef.current?.innerHTML,
      };

      try {
        promptResource
          ? await api.put<typeof data, { id: number }>(
              `/v1/distribution/subtitles-generation-prompts/${promptResource.id}`,
              data,
            )
          : await api.post<typeof data, { id: number }>(
              '/v1/distribution/subtitles-generation-prompts',
              data,
            );

        await queryClient.invalidateQueries(['subtitles_generation_prompts']);

        onClose();
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true, true);
      }
    })(e);
  };

  return (
    <Modal>
      <StyledSidePanel
        heading={promptResource ? 'Edit Prompt' : 'New Prompt'}
        onClose={onClose}
      >
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                name="title"
                type="text"
                label="Title"
                control={control}
              />
              <Larger>
                <Label>Prompt</Label>
                <PromptDiv
                  as="div"
                  ref={divRef}
                  dangerouslySetInnerHTML={{
                    __html: promptResource?.text ?? defaultPrompt,
                  }}
                />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              {promptResource ? 'Update' : 'Save'}
            </Button>
            <Button type="button" color="secondary" onClick={onClose}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </StyledSidePanel>
    </Modal>
  );
};

export default Form;
